import api from "./api"
import { CwFlaggedPunch, CwOfficeAlert, CwTimeClockSettings } from "../Interfaces"
import { throwSageApiError } from "../utils/sageApiError"

function companyUrl(companyId: number) {
  if (!companyId) throw new Error("No management company id provided")
  return `/sage-100/companies/${companyId}`
}

function list<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  const inner = (data as { data?: unknown } | null)?.data
  return Array.isArray(inner) ? (inner as T[]) : []
}

class TimeClockService {
  public getSettings = async (companyId: number): Promise<CwTimeClockSettings> => {
    try {
      const response = await api.get(`${companyUrl(companyId)}/time-clock-settings`)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not load time clock settings.")
    }
  }

  public updateSettings = async (
    companyId: number,
    body: Partial<Omit<CwTimeClockSettings, "companyId">>,
  ): Promise<CwTimeClockSettings> => {
    try {
      const response = await api.patch(`${companyUrl(companyId)}/time-clock-settings`, body)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not save time clock settings.")
    }
  }

  public listAlerts = async (companyId: number, includeClosed = false): Promise<CwOfficeAlert[]> => {
    try {
      const response = await api.get(`${companyUrl(companyId)}/office-alerts`, {
        params: includeClosed ? { includeClosed: "true" } : {},
      })
      return list<CwOfficeAlert>(response.data)
    } catch (error) {
      throwSageApiError(error, "Could not load office alerts.")
    }
  }

  public overrideAlert = async (companyId: number, alertId: number, note?: string): Promise<CwOfficeAlert> => {
    try {
      const response = await api.post(`${companyUrl(companyId)}/office-alerts/${alertId}/override`, note ? { note } : {})
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not allow the clock-in.")
    }
  }

  public dismissAlert = async (companyId: number, alertId: number): Promise<CwOfficeAlert> => {
    try {
      const response = await api.post(`${companyUrl(companyId)}/office-alerts/${alertId}/dismiss`)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not dismiss the alert.")
    }
  }

  public listFlagged = async (
    companyId: number,
    from: string,
    to: string,
    includeReviewed = false,
  ): Promise<CwFlaggedPunch[]> => {
    try {
      const response = await api.get(`${companyUrl(companyId)}/flagged-punches`, {
        params: { from, to, ...(includeReviewed ? { includeReviewed: "true" } : {}) },
      })
      return list<CwFlaggedPunch>(response.data)
    } catch (error) {
      throwSageApiError(error, "Could not load flagged punches.")
    }
  }

  public reviewPunch = async (companyId: number, punchId: number) => {
    try {
      const response = await api.post(`${companyUrl(companyId)}/flagged-punches/${punchId}/review`)
      return response.data
    } catch (error) {
      throwSageApiError(error, "Could not mark the punch reviewed.")
    }
  }
}

export const timeClockService = new TimeClockService()
